<script setup>
import { ref, computed } from "vue"

const props = defineProps({
  type: {
    type: String,
    default: "primary"
  },
  size: {
    type: [ String, Number ],
    default: "md" //sm, md, lg, xl or by percent 100, 125, 150, etc.
  },
  accesskey: {
    type: String,
    default: null
  },
  enabled: {
    type: Boolean,
    default: true
  },
  grid: { //grid action button
    type: Boolean,
    default: false
  },
  leftIcon: {
    type: String,
    default: null
  }
});

const emit = defineEmits(["click"]);

const buttonClass = computed(() => {
  let classList = [ "button-base" ];
  if (!props.enabled) {
    classList.push("button-disabled");
  }
  else if (props.type) {
    classList.push("button-" + props.type);
  }
  if (typeof props.size == "string") {
    classList.push("button-size-" + props.size);
  }
  if (typeof props.size == "number" && props.size > 0) {
    classList.push("button-size-" + props.size + " scale-" + props.size);
  }
  if (props.grid) {
    classList.push("button-grid-action");
  }
  let className = classList.join(" ");
  return className;
});

function click() {
  if (props.enabled) {
    emit("click");
  }
}
</script>

<template>
  <!-- @click="$emit('click')" may be triggered twice -->
  <button type="button" :accesskey="accesskey" :class="buttonClass" @click.prevent="click"><div class="absolute left-3 top-0 h-full grid place-items-center" v-if="props.leftIcon"><i :class="'fa fa-' + props.leftIcon"></i></div><slot /></button>
</template>

<style scoped>
.button-base {
  @apply relative;
}

.button-primary, .button-info, .button-question, .button- {
  @apply whitespace-nowrap text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800;
}

.button-success {
  @apply whitespace-nowrap text-white bg-emerald-700 hover:bg-emerald-800 focus:ring-4 focus:outline-none focus:ring-emerald-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-emerald-600 dark:hover:bg-emerald-700 dark:focus:ring-emerald-800;
}

.button-warning {
  @apply whitespace-nowrap text-yellow-800 bg-yellow-300 hover:bg-yellow-400 focus:ring-4 focus:outline-none focus:ring-yellow-200 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:text-black dark:bg-yellow-400 dark:hover:bg-yellow-400/90 dark:focus:ring-yellow-800;
}

.button-danger, .button-question-danger {
  @apply whitespace-nowrap text-white bg-red-700 hover:bg-red-800 focus:ring-4 focus:outline-none focus:ring-red-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-red-600 dark:hover:bg-red-700 dark:focus:ring-red-800;
}

.button-purple {
  @apply whitespace-nowrap text-purple-800 bg-purple-300 hover:bg-purple-400 focus:ring-4 focus:outline-none focus:ring-purple-200 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-purple-600 dark:hover:bg-purple-700 dark:focus:ring-purple-800;
}

.button-secondary {
  @apply whitespace-nowrap text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-blue-300 rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-500 dark:hover:text-white dark:hover:bg-gray-600 dark:focus:ring-gray-600;
}

.button-disabled {
  @apply whitespace-nowrap cursor-default text-gray-300 bg-white outline-none rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-500;
}

.button-grid-action {
  border: none;
  background: transparent;
  border-radius: 0;
}

.button-grid-action:hover {
  @apply bg-gray-200;
}

.button-size-sm {
  margin: 0 !important;
  padding: 0 !important;
  font-size: 12px;
  line-height: 28px;
  text-align: center;
  width: 28px;
  height: 28px;
}

.button-size-md {
  /* Default, no changes */
}

.button-size-lg {
  @apply px-5 py-3 text-lg text-center;
}

.button-size-xl {
  @apply px-7 py-6 text-center;
  font-size: 24px;
}

.items-group .button-primary,
.items-group .button-secondary,
.items-group .button-disabled {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  /* border-left: 0; */
}
</style>
